import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { SilentError } from 'init/silent-error';
import { isDefined } from 'remeda';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';
import {
  buildPreviewReminderParams,
  type PreviewReminderVariables,
} from './preview-reminder-request';

export type PreviewReminderEmailVariables = PreviewReminderVariables;

// #region Response
const ReminderEmailPreviewResponseSchema = z.object({
  data: z.object({
    email: z
      .string()
      .nullish()
      .transform(value => value ?? undefined),
  }),
});

export type ReminderEmailPreview = z.infer<typeof ReminderEmailPreviewResponseSchema>['data'];
// #endregion Response

const PLACEHOLDER: ReminderEmailPreview = { email: undefined };

// MARK: Load function
async function previewReminderEmailQueryFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: PreviewReminderEmailVariables,
): Promise<ReminderEmailPreview> {
  const instance = await axiosInstance;
  const { data } = await instance.get(`invoices/${variables.invoiceId}/reminder_email_preview`, {
    params: buildPreviewReminderParams(variables),
  });

  const parseResult = ReminderEmailPreviewResponseSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'reminderEmailPreview');
    throw new SilentError();
  }

  return parseResult.data.data;
}

// MARK: Hook
export function usePreviewReminderEmail(
  variables: PreviewReminderEmailVariables,
  options: QueryOptions<ReminderEmailPreview> = {},
) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<ReminderEmailPreview, ApiError>({
    queryKey: ['reminderEmailPreview', variables],
    queryFn: () => previewReminderEmailQueryFn(axiosInstance, variables),
    placeholderData: previousData => (isDefined(previousData) ? previousData : PLACEHOLDER),
    ...options,
  });

  return addResourceNameToQueryResult<
    ReminderEmailPreview,
    ApiError,
    'reminderEmailPreview',
    'PreventUndefined'
  >('reminderEmailPreview', queryResult, PLACEHOLDER);
}
